/**
 * The Heat system - granny's aggro model.
 *
 * DESIGN (see DESIGN.md 1.3). Three coupled values instead of one meter:
 *
 *   HEAT    0-100, global, slow. Granny's overall alarm. Drives her behavior tier.
 *   CONTACT 0-1, fast. How well she knows where you are RIGHT NOW.
 *   TRACES  spatial evidence (see traces.ts). Unresolved traces block Heat decay.
 *
 * Splitting Heat from Contact fixes the classic single-meter problem where a
 * player who was never seen still gets hunted. Granny can be highly alarmed
 * (Heat 70) yet completely blind (Contact 0) - which is exactly the tense, funny
 * state this game wants to live in.
 *
 * Everything here is a pure function over plain data: no Three.js, no Rapier, no
 * DOM. That is deliberate, so tests/unit/heat.test.ts can cover the whole
 * design in milliseconds.
 */

import type { HeatTier } from '@/core/events';
import { clamp, clamp01, remap } from '@/core/math';
import { tuning } from '@/core/tuning';

/** Ascending order of severity. Index arithmetic below relies on this. */
export const TIER_ORDER: readonly HeatTier[] = ['standby', 'suspicious', 'hunting', 'frenzy'];

export const tierIndex = (tier: HeatTier): number => TIER_ORDER.indexOf(tier);

export interface HeatState {
  /** 0-100. */
  heat: number;
  tier: HeatTier;
  /** Seconds spent in the current tier. Gates de-escalation (see below). */
  timeInTier: number;
  /** 0-1. */
  contact: number;
  /** Latches true once contact hits the spotted threshold; cleared when lost. */
  spotted: boolean;
}

export function createHeatState(): HeatState {
  return { heat: 0, tier: 'standby', timeInTier: 0, contact: 0, spotted: false };
}

/**
 * What the world looks like this step, from the heat system's point of view.
 * The caller (granny AI + world queries) is responsible for filling this in;
 * the heat system itself never queries the scene.
 */
export interface HeatContext {
  /** Granny currently has line of sight to the cat. */
  observed: boolean;
  /** The cat is inside a hide spot (box, under the bed, cupboard). */
  hidden: boolean;
  /** The cat's current noise output is below granny's hearing threshold. */
  quiet: boolean;
  /** At least one unresolved trace sits in the room granny is currently in. */
  unresolvedTraceInGrannyRoom: boolean;
}

/**
 * Which tier a given heat value implies, accounting for hysteresis.
 *
 * Escalation is IMMEDIATE and may skip tiers: drop a bookshelf in front of her
 * from standby and she goes straight to hunting. Making a player wait for
 * escalation would feel broken.
 *
 * De-escalation is gated by `minDwellSeconds` (checked by the caller in
 * `stepHeat`) and uses lower thresholds than escalation, so the tier can never
 * flap at a boundary.
 */
export function tierFor(heat: number, current: HeatTier): HeatTier {
  const { riseAt, fallAt } = tuning.heat.tiers;

  // Escalate to the highest tier this heat value qualifies for.
  let target: HeatTier = 'standby';
  if (heat >= riseAt.frenzy) target = 'frenzy';
  else if (heat >= riseAt.hunting) target = 'hunting';
  else if (heat >= riseAt.suspicious) target = 'suspicious';

  if (tierIndex(target) > tierIndex(current)) return target;

  // Otherwise consider de-escalating exactly one step, using the fall threshold
  // of the tier we are currently in.
  switch (current) {
    case 'frenzy':
      return heat < fallAt.frenzy ? 'hunting' : 'frenzy';
    case 'hunting':
      return heat < fallAt.hunting ? 'suspicious' : 'hunting';
    case 'suspicious':
      return heat < fallAt.suspicious ? 'standby' : 'suspicious';
    case 'standby':
      return 'standby';
  }
}

/** Adds heat, clamped to [0, max]. Returns the actual delta applied. */
export function addHeat(state: HeatState, amount: number): number {
  const before = state.heat;
  state.heat = clamp(state.heat + amount, 0, tuning.heat.max);
  return state.heat - before;
}

/**
 * Decay rate (heat per second) for the current context.
 *
 * The ordering encodes the central lesson of the design: escaping is not
 * enough - you must escape somewhere she is not about to look. An unresolved
 * trace in her room pins the meter no matter how well you are hidden.
 */
export function decayRateFor(ctx: HeatContext): number {
  const { decay } = tuning.heat;
  if (ctx.unresolvedTraceInGrannyRoom) return decay.unresolvedTraceInRoom;
  if (ctx.observed) return 0;
  if (ctx.hidden && ctx.quiet) return decay.hiddenAndQuiet;
  return decay.merelyUnobserved;
}

export interface HeatStepResult {
  /** Set when the tier changed this step, for the caller to emit an event. */
  tierChange?: { from: HeatTier; to: HeatTier };
}

/** Advances heat and the behavior tier by one fixed step. Mutates `state`. */
export function stepHeat(state: HeatState, ctx: HeatContext, dt: number): HeatStepResult {
  state.heat = clamp(state.heat - decayRateFor(ctx) * dt, 0, tuning.heat.max);
  state.timeInTier += dt;

  const next = tierFor(state.heat, state.tier);
  if (next === state.tier) return {};

  const escalating = tierIndex(next) > tierIndex(state.tier);
  // Rises are immediate; falls must wait out the dwell timer.
  if (!escalating && state.timeInTier < tuning.heat.tiers.minDwellSeconds) return {};

  const from = state.tier;
  state.tier = next;
  state.timeInTier = 0;
  return { tierChange: { from, to: next } };
}

/**
 * How fast Contact fills while granny has line of sight.
 *
 * Close and in the open fills in ~1.2s; far away and using Silent Paws takes
 * ~4s. That spread is what makes crouching feel like it buys you real time
 * rather than being a cosmetic state.
 */
export function contactFillRate(opts: {
  distance: number;
  crouching: boolean;
  moving: boolean;
}): number {
  const c = tuning.granny.contact;
  let rate = remap(
    opts.distance,
    c.closeDistance,
    tuning.granny.vision.range,
    c.fillRateClose,
    c.fillRateFar,
  );
  if (opts.crouching) rate *= c.crouchMultiplier;
  if (!opts.moving) rate *= c.stillMultiplier;
  return Math.max(0, rate);
}

export interface ContactContext {
  observed: boolean;
  distance: number;
  crouching: boolean;
  moving: boolean;
  /** In a hide spot AND granny did not watch the cat get in. */
  concealed: boolean;
}

/** Advances Contact by one fixed step. Mutates `state`. Returns spotted-edge info. */
export function stepContact(
  state: HeatState,
  ctx: ContactContext,
  dt: number,
): { justSpotted: boolean; justLost: boolean } {
  const c = tuning.granny.contact;
  const wasSpotted = state.spotted;

  if (ctx.observed) {
    state.contact = clamp01(
      state.contact +
        contactFillRate({ distance: ctx.distance, crouching: ctx.crouching, moving: ctx.moving }) *
          dt,
    );
  } else {
    const drain = c.drainRate * (ctx.concealed ? c.hideSpotDrainMultiplier : 1);
    state.contact = clamp01(state.contact - drain * dt);
  }

  state.spotted = state.contact >= c.spottedThreshold;

  return {
    justSpotted: state.spotted && !wasSpotted,
    justLost: !state.spotted && wasSpotted,
  };
}

/** Soft fail: reset alarm but keep the player's progress. See DESIGN.md fail state. */
export function resetHeatAfterCatch(state: HeatState): void {
  state.heat = tuning.heat.onCatch.resetTo;
  state.tier = 'standby';
  state.timeInTier = 0;
  state.contact = 0;
  state.spotted = false;
}

/** Granny's movement speed for the current tier. */
export function speedForTier(tier: HeatTier): number {
  const s = tuning.granny.speeds;
  switch (tier) {
    case 'standby':
      return s.routine;
    case 'suspicious':
      return s.investigate;
    case 'hunting':
      return s.hunt;
    case 'frenzy':
      return s.frenzy;
  }
}
